import React from 'react';
import { PageControls } from './PageControls';
import { Terminal, BookOpen, Image as ImageIcon } from 'lucide-react';

interface BookContentProps {
  chapterContent?: any;
  currentChapter: number;
  currentPage: number;
  pageContent: string[];
}

export const BookContent: React.FC<BookContentProps> = ({
  chapterContent,
  currentChapter,
  currentPage,
  pageContent
}) => {
  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      height: '700px',
      backgroundColor: '#111',
      borderRadius: '8px',
      display: 'flex',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      position: 'relative'
    }}>
      {/* Left Page - Text Content */}
      <div style={{
        flex: 1,
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative',
        backgroundColor: '#0a0a0a',
        backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 10%)'
      }}>
        {/* Header Section */}
        <div className="p-6 border-b border-gray-800 bg-black/30">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded bg-purple-500/20 text-purple-400">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm text-gray-500 font-mono">
                  CHAPTER_{currentChapter}
                </div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  {chapterContent?.title || `Chapter ${currentChapter}`}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-green-400 text-sm font-mono">STREAMING</span>
            </div>
          </div>

          <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
              style={{ width: `${(currentPage / (pageContent?.length || 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Content Area */}
        <div style={{
          position: 'relative',
          height: 'calc(100% - 140px)',
          overflow: 'hidden'
        }}>
          <div className="h-full overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              {Array.isArray(pageContent) &&
                pageContent.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-gray-300 leading-relaxed font-light"
                    style={{
                      textShadow: '0 0 10px rgba(255,255,255,0.1)'
                    }}
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
            </div>
          </div>
        </div>

        {/* Page Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
          <div className="flex items-center justify-center">
            <div className="px-4 py-2 bg-gray-900/50 rounded-lg border border-gray-800">
              <PageControls currentPage={currentPage} />
            </div>
          </div>
        </div>
      </div>

      {/* Right Page - Illustration */}
      <div style={{
        flex: 1,
        backgroundColor: '#0a0a0a',
        backgroundImage: 'linear-gradient(to left, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 10%)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Image Header */}
        <div className="p-6 border-b border-gray-800 bg-black/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded bg-blue-500/20 text-blue-400">
                <ImageIcon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm text-gray-500 font-mono">
                  ILLUSTRATION_{currentChapter}
                </div>
                <div className="text-blue-400 font-mono">
                  rendering_status: [ACTIVE]
                </div>
              </div>
            </div>
            <Terminal className="w-5 h-5 text-gray-600" />
          </div>
        </div>

        {/* Image Content */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="relative w-full h-full max-h-[500px] rounded-lg overflow-hidden">
            {/* Border Glow Effect */}
            <div className="absolute inset-0 border-2 border-purple-500/20 rounded-lg"
                 style={{ boxShadow: '0 0 20px rgba(168,85,247,0.2)' }} />

            {chapterContent?.illustration?.type === 'video' ? (
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover rounded-lg"
                style={{ filter: 'brightness(0.9) contrast(1.1)' }}
              >
                <source src={chapterContent.illustration.path} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={chapterContent?.illustration?.path || chapterContent?.imagePath}
                alt={`Illustration for ${chapterContent?.title}`}
                className="w-full h-full object-cover rounded-lg"
                style={{ filter: 'brightness(0.9) contrast(1.1)' }}
              />
            )}

            {/* Overlay Scanlines */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none" />
            <div className="absolute inset-0 bg-[url('/scanlines.png')] opacity-10 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
};
